/**
 * /Recon portal — post-commission Stripe Checkout hand-off.
 *
 * The previous magic-link engagement-letter flow was retired in
 * Bucket QL. The click-to-accept Authorization to Assess on the
 * commission form is the legal anchor; once the form submits, this
 * page immediately creates a Stripe Checkout Session server-side and
 * redirects the buyer to the Stripe-hosted payment page.
 *
 * Why auto-redirect rather than show a "click to pay" CTA:
 *   - The buyer already pressed "Submit request" knowing payment was
 *     the next step. A second confirmation click is friction without
 *     a corresponding safety gain.
 *   - The Stripe Checkout page itself shows the line item + amount
 *     before any card is entered, so the price is never hidden.
 *   - If creation fails (no attestation on file, no in-scope assets,
 *     Stripe unconfigured) the error is surfaced inline and the buyer
 *     can return to the portal.
 *
 * Price is server-derived from the case's in-scope domain target
 * count via the /Recon price bands — the buyer cannot supply or
 * override it.
 */
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  createEngagementCheckoutApiV1ClientPortalEngagementsCaseIdCheckoutPost,
  listEngagementsApiV1ClientPortalEngagementsGet,
} from "@hackereyes/api-client";
import { useClientAuth } from "@/auth/client-auth-context";

function dollarsFromCents(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function PortalCommissionSuccess() {
  const [searchParams] = useSearchParams();
  const caseNumber = searchParams.get("case") || undefined;
  const auth = useClientAuth();

  // Pattern B (Bucket M) bypassed this page entirely — the commission
  // form now redirects straight to Stripe and the case row materializes
  // inside the webhook handler. If we land here without a case number
  // (or with "undefined" as a literal string from a stale bundle that
  // still tries to navigate here), bounce to /portal rather than
  // rendering "We've got it. undefined". The legacy Pattern A flow
  // (Operative-created engagement awaiting buyer payment) still uses
  // this page with a real case number from engagement-detail.tsx.
  if (!caseNumber || caseNumber === "undefined") {
    return <Navigate to="/portal" replace />;
  }

  // The URL gives us the case number; the engagements list gives us
  // the case id. We don't put the case id on the URL because case
  // numbers are how the buyer refers to engagements ("SC-RC-2026-001")
  // and that's what the success page prints.
  const [caseId, setCaseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"resolving" | "creating" | "redirecting" | "error">(
    "resolving",
  );
  const [amount, setAmount] = useState<{ cents: number; assets: number } | null>(null);

  // Guard against StrictMode double-fire — checkout creation is not
  // idempotent on Stripe's side (two POSTs = two sessions). The ref
  // is checked synchronously inside the effect so the second mount
  // bails before posting.
  const dispatchedRef = useRef(false);

  // 1. Resolve case_id from case_number via the engagements list.
  //    Could also POST a "look up by number" endpoint but the list
  //    is small and already authenticated.
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (!caseNumber) {
        if (!cancelled) {
          setError("Missing case reference in URL.");
          setPhase("error");
        }
        return;
      }
      try {
        const { data, error: e } = await listEngagementsApiV1ClientPortalEngagementsGet();
        if (e || !data) throw new Error("Failed to load engagements.");
        const rows = data as Array<{ id: string; case_number: string }>;
        const match = rows.find((r) => r.case_number === caseNumber);
        if (!match) {
          if (!cancelled) {
            setError(`Couldn't locate engagement ${caseNumber}.`);
            setPhase("error");
          }
          return;
        }
        if (!cancelled) setCaseId(match.id);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to resolve engagement.");
          setPhase("error");
        }
      }
    }
    if (auth.me && !cancelled) resolve();
    return () => {
      cancelled = true;
    };
  }, [caseNumber, auth.me]);

  // 2. Once we have the case id, POST /checkout and redirect.
  useEffect(() => {
    if (!caseId || dispatchedRef.current) return;
    dispatchedRef.current = true;
    setPhase("creating");

    (async () => {
      try {
        const { data, error: e } =
          await createEngagementCheckoutApiV1ClientPortalEngagementsCaseIdCheckoutPost({
            path: { case_id: caseId },
          });
        if (e || !data) {
          const detail =
            (e as { detail?: string } | undefined)?.detail ??
            "Couldn't create the payment session.";
          throw new Error(detail);
        }
        setAmount({ cents: data.amount_cents, assets: data.asset_count });
        setPhase("redirecting");
        // Tiny delay so the price is visible for a beat before
        // Stripe's page replaces ours.
        window.setTimeout(() => {
          window.location.assign(data.checkout_url);
        }, 700);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Checkout failed.");
        setPhase("error");
      }
    })();
  }, [caseId]);

  return (
    <div className="recon-page">
      <section className="recon-hero">
        <div className="recon-hero-inner">
          <div className="eyebrow recon-hero-eyebrow">/RECON · ENGAGEMENT COMMISSIONED</div>
          <h1 className="recon-hero-title">
            We've got it.{" "}
            {caseNumber && <span className="text-flame">{caseNumber}</span>}
          </h1>

          {(phase === "resolving" || phase === "creating") && (
            <p className="recon-hero-sub">Preparing your secure checkout…</p>
          )}

          {phase === "redirecting" && amount && (
            <>
              <p className="recon-hero-sub">
                Total: <strong>{dollarsFromCents(amount.cents)}</strong>
                {" "}for an assessment of {amount.assets}{" "}
                domain target{amount.assets === 1 ? "" : "s"}.
              </p>
              <p className="recon-hero-sub" style={{ opacity: 0.7 }}>
                Redirecting you to Stripe…
              </p>
            </>
          )}

          {phase === "error" && (
            <>
              <p className="recon-hero-sub" style={{ color: "var(--flame-300)" }}>
                {error}
              </p>
              <div className="recon-hero-cta">
                <Link
                  to="/portal"
                  className="recon-btn recon-btn-primary recon-btn-lg"
                >
                  Back to portal
                </Link>
              </div>
            </>
          )}

          {caseNumber && phase !== "error" && (
            <div className="recon-hero-trust">
              <span className="trust-dot" />
              <span>Save your engagement reference: {caseNumber}</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
